<script lang="ts">
	import type { Snippet } from 'svelte';
	import { Button, IconButton } from '@andresot/ui';
	import { fade, scale } from 'svelte/transition';

	interface Props {
		title: string;
		description?: string;
		confirmLabel?: string;
		cancelLabel?: string;
		/** Красная кнопка подтверждения — для удаления и выхода. */
		danger?: boolean;
		wide?: boolean;
		busy?: boolean;
		allowCancelWhileBusy?: boolean;
		confirmDisabled?: boolean;
		onconfirm?: () => void;
		oncancel: () => void;
		children?: Snippet;
		/** Отдельный слой для форм внутри преобразованных мобильных панелей. */
		portal?: boolean;
	}

	let {
		title,
		description,
		confirmLabel = 'Подтвердить',
		cancelLabel = 'Отмена',
		danger = false,
		wide = false,
		busy = false,
		allowCancelWhileBusy = false,
		confirmDisabled = false,
		onconfirm,
		oncancel,
		children,
		portal = false
	}: Props = $props();

	let dialog = $state<HTMLDivElement | null>(null);
	function outsidePanel(node: HTMLElement, enabled: boolean) {
		if (!enabled) return;
		document.body.append(node);
		return { destroy: () => node.remove() };
	}

	function cancel(): void {
		if (!busy || allowCancelWhileBusy) oncancel();
	}

	/*
	 * Ловушка фокуса. Пока окно открыто, Tab не должен уводить в интерфейс
	 * под ним — иначе пользователь клавиатуры «проваливается» в недоступную
	 * страницу и не понимает, где находится.
	 */
	function trapFocus(event: KeyboardEvent): void {
		if (event.key === 'Escape') {
			event.preventDefault();
			cancel();
			return;
		}

		if (event.key !== 'Tab' || !dialog) return;

		const focusable = dialog.querySelectorAll<HTMLElement>(
			'button:not(:disabled), [href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])'
		);
		if (focusable.length === 0) return;

		const first = focusable[0]!;
		const last = focusable[focusable.length - 1]!;

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	}

	$effect(() => {
		const previous = document.activeElement as HTMLElement | null;

		dialog?.querySelector<HTMLElement>('button')?.focus();

		// Возвращаем фокус туда, откуда окно открыли.
		return () => previous?.focus();
	});
</script>

<div
	use:outsidePanel={portal}
	class="backdrop"
	role="presentation"
	transition:fade={{ duration: 150 }}
	onpointerdown={(e) => {
		if (e.target === e.currentTarget) cancel();
	}}
>
	<div
		bind:this={dialog}
		class="dialog"
		class:wide
		role="dialog"
		aria-modal="true"
		aria-label={title}
		tabindex="-1"
		transition:scale={{ duration: 180, start: 0.96 }}
		onkeydown={trapFocus}
	>
		<header class="head">
			<h2 class="title">{title}</h2>
			<IconButton
				icon="close"
				label="Закрыть"
				size={36}
				disabled={busy && !allowCancelWhileBusy}
				onclick={cancel}
			/>
		</header>

		{#if description}
			<p class="description">{description}</p>
		{/if}

		{#if children}
			<div class="body">{@render children()}</div>
		{/if}

		<footer class="actions">
			<Button variant="secondary" disabled={busy && !allowCancelWhileBusy} onclick={cancel}
				>{cancelLabel}</Button
			>
			{#if onconfirm}
				<Button
					variant={danger ? 'danger' : 'primary'}
					disabled={busy || confirmDisabled}
					loading={busy}
					onclick={onconfirm}
				>
					{confirmLabel}
				</Button>
			{/if}
		</footer>
	</div>
</div>

<style>
	.dialog.wide {
		max-width: 960px;
	}
	.backdrop {
		position: fixed;
		inset: 0;
		z-index: var(--z-modal);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--spacing-l);
		background: var(--background-overlay);
		backdrop-filter: blur(4px);
	}

	.dialog {
		display: flex;
		flex-direction: column;
		width: 100%;
		max-width: 420px;
		max-height: calc(100dvh - var(--spacing-l) * 2);
		padding: var(--spacing-l);
		border-radius: var(--radius-modal);
		background: var(--background-card);
		box-shadow: var(--shadow-l);
		outline: none;
	}

	.head {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-m);
		margin-bottom: var(--spacing-s);
	}

	.title {
		font: var(--text-subtitle);
		font-size: var(--font-size-xl);
	}

	.description {
		color: var(--text-secondary);
		font: var(--text-body);
		font-size: var(--font-size-m);
	}

	.body {
		margin-top: var(--spacing-m);
		min-height: 0;
		overflow-y: auto;
	}

	.actions {
		flex-shrink: 0;
		display: flex;
		justify-content: flex-end;
		gap: var(--spacing-s);
		margin-top: var(--spacing-xl);
	}

	@media (max-width: 680px) {
		.backdrop {
			align-items: flex-end;
			padding: 0;
		}

		.dialog {
			max-width: none;
			max-height: 88dvh;
			overflow-y: auto;
			padding: var(--spacing-xl) var(--spacing-l) max(var(--spacing-l), env(safe-area-inset-bottom));
			border-radius: var(--radius-modal) var(--radius-modal) 0 0;
		}

		.dialog::before {
			content: '';
			align-self: center;
			width: 42px;
			height: 4px;
			margin: calc(var(--spacing-m) * -1) 0 var(--spacing-m);
			border-radius: 2px;
			background: var(--border-strong);
		}

		.actions {
			flex-direction: column-reverse;
		}

		.actions :global(button) {
			width: 100%;
		}
	}
</style>
